import React from 'react';

const selectClass =
  'h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60';
const inputClass =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * "Sorteos" on/off toggle + "Selección de números" select + "Mostrar números al comprador"
 * toggle (NuevaRifa / EditRifa). Hidden numbers force the random picker (select disabled).
 */
export function SorteosPickerFields({
  t,
  sorteosEnabled,
  onSorteosChange,
  pickerMode,
  onPickerChange,
  mostrarNumeros = true,
  onMostrarNumerosChange,
}) {
  return (
    <div className='grid gap-4 sm:grid-cols-2'>
      <div className='space-y-1.5'>
        <label className='flex items-center gap-2 text-sm font-medium text-foreground'>
          <input
            type='checkbox'
            className='h-4 w-4 cursor-pointer'
            checked={sorteosEnabled}
            onChange={e => onSorteosChange(e.target.checked)}
          />
          <strong>{t('raffle.sorteosEnabled', 'Sorteos')}</strong>
        </label>
        <p className='text-xs text-muted-foreground'>
          {t(
            'raffle.sorteosEnabledHint',
            'Si se desactiva, no se muestran sorteos ni premios y las casillas pueden no incluir números.'
          )}
        </p>
      </div>
      <div className='space-y-1.5'>
        <label className='text-sm font-medium text-foreground'>
          <strong>{t('raffle.pickerMode', 'Selección de números')}</strong>
        </label>
        <select
          className={selectClass}
          value={mostrarNumeros ? pickerMode : 'random'}
          disabled={!mostrarNumeros}
          onChange={e => onPickerChange(e.target.value)}
        >
          <option value='manual'>{t('raffle.pickerModeManual', 'Manual')}</option>
          <option value='random'>{t('raffle.pickerModeRandom', 'Aleatoria')}</option>
          <option value='both'>{t('raffle.pickerModeBoth', 'Ambas')}</option>
        </select>
        {!mostrarNumeros && (
          <p className='text-xs text-muted-foreground'>
            {t(
              'raffle.pickerModeForcedRandom',
              'Con los números ocultos, se asignan siempre al azar.'
            )}
          </p>
        )}
      </div>
      {onMostrarNumerosChange && (
        <div className='space-y-1.5 sm:col-span-2'>
          <label className='flex items-center gap-2 text-sm font-medium text-foreground'>
            <input
              type='checkbox'
              className='h-4 w-4 cursor-pointer'
              checked={mostrarNumeros}
              onChange={e => onMostrarNumerosChange(e.target.checked)}
            />
            <strong>{t('raffle.mostrarNumeros', 'Mostrar números al comprador')}</strong>
          </label>
          <p className='text-xs text-muted-foreground'>
            {t(
              'raffle.mostrarNumerosHint',
              'Si se desactiva, el comprador no elige ni ve sus números: se asignan al azar y solo los ven los administradores.'
            )}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Board layout: one grid per category, or one shared grid where categories are plans.
 * `sharedBoard` = { filas, columnas } (only shown for the shared mode).
 */
export function BoardModeFields({
  t,
  boardMode,
  onModeChange,
  sharedBoard,
  onSharedBoardChange,
  modeLocked,
}) {
  const shared = boardMode === 'shared';
  return (
    <div className='space-y-3 rounded-lg border border-border p-3'>
      <div className='space-y-1.5'>
        <label className='text-sm font-medium text-foreground'>
          <strong>{t('raffle.boardMode', 'Categorías')}</strong>
        </label>
        <select
          className={selectClass}
          value={shared ? 'shared' : 'separate'}
          disabled={modeLocked}
          onChange={e => onModeChange(e.target.value)}
        >
          <option value='separate'>
            {t('raffle.boardModeSeparate', 'Cada una con su propio tablero')}
          </option>
          <option value='shared'>
            {t('raffle.boardModeShared', 'Dentro de un mismo tablero')}
          </option>
        </select>
        <p className='text-xs text-muted-foreground'>
          {modeLocked
            ? t(
                'raffle.boardModeLocked',
                'No se puede cambiar mientras haya casillas reservadas o vendidas.'
              )
            : shared
              ? t(
                  'raffle.boardModeSharedHint',
                  'Un solo tablero: al comprar, el cliente elige un plan para cada casilla.'
                )
              : t(
                  'raffle.boardModeSeparateHint',
                  'Cada categoría tiene su propio tablero y precio.'
                )}
        </p>
      </div>
      {shared && (
        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='space-y-1.5'>
            <label className='text-sm font-medium text-foreground'>
              {t('raffle.rows', 'Filas')}
            </label>
            <input
              type='number'
              min='1'
              className={inputClass}
              value={sharedBoard.filas}
              onChange={e =>
                onSharedBoardChange({
                  ...sharedBoard,
                  filas: Math.max(1, parseInt(e.target.value) || 1),
                })
              }
            />
          </div>
          <div className='space-y-1.5'>
            <label className='text-sm font-medium text-foreground'>
              {t('raffle.columns', 'Columnas')}
            </label>
            <input
              type='number'
              min='1'
              className={inputClass}
              value={sharedBoard.columnas}
              onChange={e =>
                onSharedBoardChange({
                  ...sharedBoard,
                  columnas: Math.max(1, parseInt(e.target.value) || 1),
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
